import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSchool } from "../Hooks/SchoolContext";

/**
 * Holds anyone who still owes setup on /school/onboarding: an independent
 * instructor until their group can work, a school's managers until the school
 * can be paid and has its first student, and a school's instructors until
 * their teaching profile is ready. Which steps each person owes is decided on
 * the server (web-backend utils/onboarding_steps.js), not here. Mirrors OnboardingGuard in web-frontend's _app.js: redirect
 * rather than hide, so there is one place the unfinished setup lives and no
 * way to end up on a page that cannot function yet.
 *
 * THREE THINGS IT DELIBERATELY DOES NOT DO
 * - It never redirects while `onboarding` is null (still loading). Bouncing on
 *   an unknown answer would yank someone mid-navigation on a slow connection.
 * - It never redirects when `applies` is false. The front desk, a group's
 *   second team member, or god mode must be untouched by this.
 * - It fails open everywhere. The status endpoint answers "complete" on error,
 *   so a broken check cannot lock anyone out of their own portal.
 */

// The only page inside this layout that stays reachable mid-setup. Stripe
// sends the browser back here, and bouncing it would abandon the status poll
// that records the payouts step as done. Everything else — including the
// dashboard — waits until setup is finished, because every step can now be
// completed on the onboarding page itself.
const ALLOWED_WHILE_ONBOARDING = ["/school/tutors/connect-return"];

const isAllowed = (pathname) =>
  ALLOWED_WHILE_ONBOARDING.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export default function InstructorOnboardingGuard({ children }) {
  const { onboarding } = useSchool();
  const location = useLocation();

  if (!onboarding) return children;            // not known yet
  if (!onboarding.applies) return children;    // not a gated account
  if (onboarding.complete) return children;
  if (isAllowed(location.pathname)) return children;

  return <Navigate to="/school/onboarding" replace />;
}
